import useTranslation from '../../../i18next.config';
import {useAgentService} from '../../../services/agent';
import {useCallback, useEffect, useState} from 'react';
import {Agent} from '../../../types/models/Agent';
import {Anchor, Badge, Button, Paper, Table} from '@mantine/core';
import {ERoutes} from '../../../types/enum/routes.enum';
import {useNavigate} from 'react-router-dom';
import {showNotification} from '@mantine/notifications';
import ConfirmationModal from '../../core/ConfirmationModals';

const isOnline = (agent: Agent) => {
    if (!agent.lastSeenAt) return false;
    return (Date.now() - new Date(agent.lastSeenAt).getTime()) < 2 * 60 * 1000;
};

const formatLastSeen = (agent: Agent) => {
    if (!agent.lastSeenAt) return 'never';
    const seconds = Math.floor((Date.now() - new Date(agent.lastSeenAt).getTime()) / 1000);
    if (seconds < 60) return `${seconds} seconds ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days !== 1 ? 's' : ''} ago`;
};

const getAgentStatus = (agent: Agent): {label: string; color: string} => {
    if (!agent.active) return {label: 'Disabled', color: 'gray'};
    if (agent.running) return {label: 'Running', color: 'blue'};
    if (isOnline(agent)) return {label: 'Online', color: 'green'};
    return {label: 'Offline', color: 'red'};
};

export function AgentsList() {
    const {t} = useTranslation();
    const agentService = useAgentService();
    const navigate = useNavigate();

    const [agents, setAgents] = useState<Agent[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedAgentId, setSelectedAgentId] = useState('');

    const fetchAgents = useCallback(() => {
        agentService
            .getAgents({
                error: error => {
                    console.error(error);
                },
                success: res => {
                    return res.data;
                },
            })
            .then((agentList: Agent[]) => {
                setAgents(agentList);
            });
    }, []);

    useEffect(() => {
        fetchAgents();
        const interval = setInterval(fetchAgents, 30000);
        return () => clearInterval(interval);
    }, [fetchAgents]);

    function deleteAgent(id: string) {
        agentService.deleteAgent(
            {
                error: error => {
                    showNotification({
                        title: t('agent.deleteAgentNotification'),
                        message: error ? error.message : t('agent.deleteAgentErrorMessage'),
                        color: 'red',
                    });
                },
                success: () => {
                    showNotification({
                        title: t('agent.deleteAgentNotification'),
                        message: t('agent.deleteAgentSuccessMessage'),
                        color: 'green',
                    });
                    setAgents(agents.filter(agent => agent._id !== id));
                },
            },
            id
        );
    }

    const agentsArray = agents ? agents : [];

    const rows = agentsArray.map((agent: Agent) => (
        <Table.Tr key={agent._id}>
            <Table.Td>{agent.reference}</Table.Td>
            <Table.Td>{agent.type}</Table.Td>
            <Table.Td>
                <span
                    title={agent.url}
                    style={{
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 250,
                        display: 'inline-block',
                    }}
                >
                    {agent.url}
                </span>
            </Table.Td>
            <Table.Td>
                <Badge color={getAgentStatus(agent).color}>
                    {getAgentStatus(agent).label}
                </Badge>
            </Table.Td>
            <Table.Td>{formatLastSeen(agent)}</Table.Td>
            <Table.Td>
                {agent.currentExecutionId ? (
                    <Anchor
                        size="sm"
                        onClick={() => navigate(`/executions/${agent.currentExecutionId}`)}
                        style={{cursor: 'pointer'}}
                    >
                        {agent.currentExecutionId}
                    </Anchor>
                ) : (
                    '—'
                )}
            </Table.Td>
            <Table.Td>
                <Button
                    variant='outline'
                    onClick={() => {
                        navigate(ERoutes.ADMIN_UPDATE_AGENT, {
                            state: {agentToUpdate: agent},
                        });
                    }}
                >
                    {t('agent.editAgent')}
                </Button>
            </Table.Td>
            <Table.Td>
                <Button
                    color="red"
                    variant="outline"
                    onClick={() => {
                        setSelectedAgentId(agent._id);
                        setIsModalOpen(true);
                    }}
                >
                    {t('agent.deleteAgent')}
                </Button>
            </Table.Td>
        </Table.Tr>
    ));

    return (
        <>
            <Button
                color="cyan.8"
                type="submit"
                style={{marginBottom: 20}}
                onClick={() => {navigate(ERoutes.ADMIN_ADD_AGENT);}}>
                {t('agent.addAgentBtn')}
            </Button>

            <Paper shadow="md" style={{width: 'fit-content', margin: 'auto'}}>
                <Table.ScrollContainer minWidth={700}>
                    <Table highlightOnHover>
                        <Table.Thead>
                        <Table.Tr>
                            <Table.Th style={{minWidth: '100px'}}>{t('agent.name')}</Table.Th>
                            <Table.Th style={{minWidth: '100px'}}>{t('agent.type')}</Table.Th>
                            <Table.Th style={{minWidth: '140px'}}>{t('agent.url')}</Table.Th>
                            <Table.Th style={{minWidth: '100px'}}>{t('agent.status')}</Table.Th>
                            <Table.Th style={{minWidth: '120px'}}>Last Seen</Table.Th>
                            <Table.Th style={{minWidth: '120px'}}>{t('agent.execution')}</Table.Th>
                            <Table.Th>{t('actions')}</Table.Th>
                        </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>{rows}</Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </Paper>
            <ConfirmationModal
                isOpen={isModalOpen}
                title={t('agent.deleteAgent')}
                text={t('areYouSure')}
                onClose={() => setIsModalOpen(false)}
                onConfirmation={() => {
                    setIsModalOpen(false);
                    deleteAgent(selectedAgentId);
                }}
            />
        </>
    );
}
